import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { 
  Calculator, 
  Loader2,
  Copy,
  Download,
  Users,
  Building2,
  Trophy,
  Gift,
  Euro,
  Calendar,
  CheckCircle,
  AlertCircle,
  Briefcase,
  FileText,
  Printer
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatCurrency = (value) => {
  return new Intl.NumberFormat('pt-PT', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value || 0);
};

const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  return new Date(dateStr).toLocaleDateString('pt-PT', {
    day: '2-digit',
    month: 'long',
    year: 'numeric'
  });
};

const ProposalPage = () => {
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [simulation, setSimulation] = useState(null);
  const [error, setError] = useState(null);
  const printRef = useRef(null);

  useEffect(() => {
    loadSimulation();
  }, [id]);

  const loadSimulation = async () => {
    try {
      const response = await axios.get(`${API}/simulations/${id}`);
      setSimulation(response.data);
    } catch (err) {
      setError("Proposta não encontrada ou link inválido");
    } finally {
      setLoading(false);
    }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    toast.success("Link copiado!");
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <Card className="max-w-md">
          <CardContent className="p-8 text-center">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2">Erro</h2>
            <p className="text-muted-foreground mb-4">{error}</p>
            <Link to="/">
              <Button>Voltar ao Início</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  const result = simulation?.results?.results?.[0];
  const formData = simulation?.simulation_data || {};
  const businessDecision = simulation?.results?.business_decision;
  const premium = businessDecision?.premium;

  // Cálculos de benefícios
  const mealAllowanceDaily = formData.meal_allowance_daily || 0;
  const mealAllowanceMonthly = mealAllowanceDaily * 22;
  const mealAllowanceAnnual = mealAllowanceMonthly * 12;
  const healthInsurance = formData.health_insurance_cost || 0;
  const healthInsuranceAnnual = healthInsurance * 12;
  const ticketInfancia = formData.ticket_infancia || 0;
  const ticketInfanciaMonths = formData.ticket_infancia_months || 12;
  const ticketInfanciaAnnual = ticketInfancia * ticketInfanciaMonths;
  const ppr = formData.insurance_capitalization || 0;
  const pprMonths = formData.insurance_capitalization_months || 12;
  const pprAnnual = ppr * pprMonths;
  const totalBenefitsAnnual = mealAllowanceAnnual + healthInsuranceAnnual + ticketInfanciaAnnual + pprAnnual;

  // Valores anuais
  const grossAnnual = (result?.gross_monthly || 0) * 14;
  const netAnnual = result?.net_annual ?? ((result?.net_monthly || 0) * 12);
  const irsAnnual = (result?.irs_retention || 0) * 14;
  const ssEmployeeAnnual = (result?.social_security_employee || 0) * 14;
  
  // Líquido com benefícios
  const netWithBenefitsMonthly = (result?.net_monthly || 0) + ticketInfancia + ppr;
  // result.net_annual já inclui meal + km + ppr + ticket + coverflex — não voltar a somar (evita double counting)
  const netWithBenefitsAnnual = netAnnual;

  return (
    <div className="min-h-screen bg-white" data-testid="proposal-page">
      {/* Header - não imprime */}
      <header className="sticky top-0 z-50 bg-white border-b border-slate-200 print:hidden">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img
              src={`${process.env.REACT_APP_BACKEND_URL}/api/static/evolutt_logo.png`}
              alt="Evolutt"
              className="h-8"
              data-testid="proposal-logo"
            />
            <div>
              <p className="text-xs text-slate-500">Proposta Financeira</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={copyLink}>
              <Copy className="w-4 h-4 mr-2" />
              Copiar Link
            </Button>
            <Button variant="outline" size="sm" onClick={handlePrint}>
              <Printer className="w-4 h-4 mr-2" />
              Imprimir / PDF
            </Button>
          </div>
        </div>
      </header>

      {/* Conteúdo da Proposta */}
      <main ref={printRef} className="max-w-4xl mx-auto px-6 py-8 print:py-4">
        {/* Cabeçalho da Proposta */}
        <div className="text-center mb-8 print:mb-6">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-lg bg-[#1a1a2e] flex items-center justify-center print:bg-slate-800">
              <Calculator className="w-6 h-6 text-[#CCFF00]" />
            </div>
            <h1 className="text-3xl font-bold text-slate-900">Proposta Financeira</h1>
          </div>
          <p className="text-slate-500">
            Gerada em {formatDate(simulation?.created_at || new Date().toISOString())}
          </p>
        </div>

        {/* Dados do Candidato */}
        <Card className="mb-6 border-blue-200 bg-blue-50 print:bg-blue-50">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2 text-blue-800">
              <Users className="w-5 h-5" />
              Dados do Candidato
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-blue-600">Nome</p>
                <p className="font-semibold text-slate-900">{simulation?.name || "Não definido"}</p>
              </div>
              <div>
                <p className="text-xs text-blue-600">Data de Nascimento</p>
                <p className="font-semibold text-slate-900">{formatDate(simulation?.birth_date) || "-"}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Score e Premium */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          {result && (
            <Card className={`border-2 ${
              result.score_color === 'green' ? 'border-brand-500 bg-brand-50' :
              result.score_color === 'lime' ? 'border-lime-500 bg-lime-50' :
              result.score_color === 'yellow' ? 'border-yellow-500 bg-yellow-50' :
              result.score_color === 'orange' ? 'border-orange-500 bg-orange-50' :
              'border-red-500 bg-red-50'
            }`}>
              <CardContent className="p-4 text-center">
                <p className="text-sm text-slate-600">Score do Negócio</p>
                <p className="text-3xl font-bold">{result.score_label}</p>
                <p className="text-lg font-mono">{result.margin_percentage?.toFixed(1)}%</p>
              </CardContent>
            </Card>
          )}
          
          {premium && premium.premium_value > 0 && (
            <Card className="border-amber-300 bg-amber-50">
              <CardContent className="p-4 text-center">
                <div className="flex items-center justify-center gap-2 mb-1">
                  <Trophy className="w-5 h-5 text-amber-600" />
                  <span className="font-bold text-amber-800">{premium.tier}</span>
                </div>
                <p className="text-2xl font-mono font-bold text-amber-700">
                  {formatCurrency(premium.premium_value)}
                </p>
                <p className="text-xs text-amber-600">
                  {premium.value_type === "fixed" ? "Valor fixo" : `${premium.premium_percentage}% da margem`}
                </p>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Detalhe do Colaborador */}
        <Card className="mb-6">
          <CardHeader className="pb-2 bg-slate-50">
            <CardTitle className="text-base flex items-center gap-2">
              <Users className="w-5 h-5 text-slate-600" />
              Detalhe do Colaborador
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-4">
            {/* Mensal */}
            <div className="mb-4">
              <h4 className="text-sm font-semibold text-slate-500 mb-2">VALORES MENSAIS</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between p-2 bg-slate-50 rounded">
                  <span>Salário Bruto Mensal</span>
                  <span className="font-mono font-bold">{formatCurrency(result?.gross_monthly)}</span>
                </div>
                <div className="flex justify-between p-2 bg-red-50 rounded">
                  <span>IRS ({result?.gross_monthly ? ((result.irs_retention / result.gross_monthly) * 100).toFixed(1) : 0}%)</span>
                  <span className="font-mono text-red-600">-{formatCurrency(result?.irs_retention)}</span>
                </div>
                <div className="flex justify-between p-2 bg-red-50 rounded">
                  <span>Segurança Social (11%)</span>
                  <span className="font-mono text-red-600">-{formatCurrency(result?.social_security_employee)}</span>
                </div>
                <div className="flex justify-between p-2 bg-brand-100 rounded font-semibold">
                  <span>Líquido Mensal</span>
                  <span className="font-mono text-brand-700">{formatCurrency(result?.net_monthly)}</span>
                </div>
              </div>
            </div>

            {/* Benefícios */}
            {(mealAllowanceMonthly > 0 || ticketInfancia > 0 || ppr > 0) && (
              <div className="mb-4">
                <h4 className="text-sm font-semibold text-pink-600 mb-2">+ BENEFÍCIOS MENSAIS</h4>
                <div className="space-y-2 text-sm">
                  {mealAllowanceMonthly > 0 && (
                    <div className="flex justify-between p-2 bg-pink-50 rounded">
                      <span>Subsídio Refeição ({mealAllowanceDaily}€/dia × 22)</span>
                      <span className="font-mono text-pink-600">+{formatCurrency(mealAllowanceMonthly)}</span>
                    </div>
                  )}
                  {healthInsurance > 0 && (
                    <div className="flex justify-between p-2 bg-pink-50 rounded">
                      <span>Seguro Saúde</span>
                      <span className="font-mono text-pink-600">+{formatCurrency(healthInsurance)}</span>
                    </div>
                  )}
                  {ticketInfancia > 0 && (
                    <div className="flex justify-between p-2 bg-pink-50 rounded">
                      <span>Ticket Infância</span>
                      <span className="font-mono text-pink-600">+{formatCurrency(ticketInfancia)}</span>
                    </div>
                  )}
                  {ppr > 0 && (
                    <div className="flex justify-between p-2 bg-pink-50 rounded">
                      <span>PPR</span>
                      <span className="font-mono text-pink-600">+{formatCurrency(ppr)}</span>
                    </div>
                  )}
                  <div className="flex justify-between p-2 bg-brand-100 rounded font-semibold">
                    <span>Líquido com Benefícios</span>
                    <span className="font-mono text-brand-700">{formatCurrency(netWithBenefitsMonthly)}</span>
                  </div>
                </div>
              </div>
            )}

            <Separator className="my-4" />

            {/* Anual */}
            <div>
              <h4 className="text-sm font-semibold text-slate-500 mb-2">VALORES ANUAIS</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between p-2 bg-slate-50 rounded">
                  <span>Salário Bruto Anual (14 meses)</span>
                  <span className="font-mono font-bold">{formatCurrency(grossAnnual)}</span>
                </div>
                <div className="flex justify-between p-2 bg-brand-50 rounded">
                  <span>Salário Líquido Anual (14 meses)</span>
                  <span className="font-mono font-bold text-brand-700">{formatCurrency(grossAnnual - irsAnnual - ssEmployeeAnnual)}</span>
                </div>
                <div className="flex justify-between p-2 bg-red-50 rounded">
                  <span>IRS Anual</span>
                  <span className="font-mono text-red-600">-{formatCurrency(irsAnnual)}</span>
                </div>
                <div className="flex justify-between p-2 bg-red-50 rounded">
                  <span>SS Anual</span>
                  <span className="font-mono text-red-600">-{formatCurrency(ssEmployeeAnnual)}</span>
                </div>
                {totalBenefitsAnnual > 0 && (
                  <>
                    <h4 className="text-sm font-semibold text-pink-500 mt-3 mb-1">BENEFÍCIOS ANUAIS</h4>
                    {mealAllowanceAnnual > 0 && (
                      <div className="flex justify-between p-2 bg-pink-50 rounded">
                        <span>Subsídio Refeição (12 meses)</span>
                        <span className="font-mono text-pink-600">+{formatCurrency(mealAllowanceAnnual)}</span>
                      </div>
                    )}
                    {healthInsuranceAnnual > 0 && (
                      <div className="flex justify-between p-2 bg-pink-50 rounded">
                        <span>Seguro Saúde (12 meses)</span>
                        <span className="font-mono text-pink-600">+{formatCurrency(healthInsuranceAnnual)}</span>
                      </div>
                    )}
                    {ticketInfanciaAnnual > 0 && (
                      <div className="flex justify-between p-2 bg-pink-50 rounded">
                        <span>Ticket Infância ({ticketInfanciaMonths} meses)</span>
                        <span className="font-mono text-pink-600">+{formatCurrency(ticketInfanciaAnnual)}</span>
                      </div>
                    )}
                    {pprAnnual > 0 && (
                      <div className="flex justify-between p-2 bg-pink-50 rounded">
                        <span>Real Vida ({pprMonths} meses)</span>
                        <span className="font-mono text-pink-600">+{formatCurrency(pprAnnual)}</span>
                      </div>
                    )}
                  </>
                )}
                <div className="flex justify-between p-2 bg-pink-50 rounded">
                  <span>Subsídio Férias + Natal</span>
                  <span className="font-mono text-pink-600">+{formatCurrency((result?.gross_monthly || 0) * 2)}</span>
                </div>
                <div className="flex justify-between p-2 bg-brand-100 rounded font-semibold text-base">
                  <span>Líquido Anual Total</span>
                  <span className="font-mono text-brand-700">{formatCurrency(netWithBenefitsAnnual)}</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Detalhe da Empresa */}
        <Card className="mb-6">
          <CardHeader className="pb-2 bg-slate-50">
            <CardTitle className="text-base flex items-center gap-2">
              <Building2 className="w-5 h-5 text-slate-600" />
              Detalhe da Empresa
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-4">
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div className="p-4 bg-blue-50 rounded-lg text-center">
                <p className="text-xs text-blue-600">Receita Mensal</p>
                <p className="text-2xl font-bold font-mono text-blue-700">{formatCurrency(result?.client_revenue)}</p>
              </div>
              <div className="p-4 bg-orange-50 rounded-lg text-center">
                <p className="text-xs text-orange-600">Custo Total Mensal</p>
                <p className="text-2xl font-bold font-mono text-orange-700">{formatCurrency(result?.total_company_cost)}</p>
              </div>
            </div>

            <h4 className="text-sm font-semibold text-orange-600 mb-2">DESPESAS MENSAIS</h4>
            <div className="space-y-2 text-sm mb-4">
              <div className="flex justify-between p-2 bg-slate-50 rounded">
                <span>Salário Bruto</span>
                <span className="font-mono">{formatCurrency(result?.gross_monthly)}</span>
              </div>
              <div className="flex justify-between p-2 bg-orange-50 rounded">
                <span>SS Entidade Patronal (23.75%)</span>
                <span className="font-mono text-orange-600">{formatCurrency(result?.social_security_employer)}</span>
              </div>
              {result?.costs_breakdown?.map((cost, idx) => (
                <div key={idx} className="flex justify-between p-2 bg-orange-50 rounded">
                  <span>{cost.name}</span>
                  <span className="font-mono text-orange-600">{formatCurrency(cost.value)}</span>
                </div>
              ))}
              {mealAllowanceMonthly > 0 && (
                <div className="flex justify-between p-2 bg-pink-50 rounded">
                  <span>Subsídio Refeição</span>
                  <span className="font-mono text-pink-600">{formatCurrency(mealAllowanceMonthly)}</span>
                </div>
              )}
              {healthInsurance > 0 && (
                <div className="flex justify-between p-2 bg-pink-50 rounded">
                  <span>Seguro Saúde</span>
                  <span className="font-mono text-pink-600">{formatCurrency(healthInsurance)}</span>
                </div>
              )}
              {ticketInfancia > 0 && (
                <div className="flex justify-between p-2 bg-pink-50 rounded">
                  <span>Ticket Infância</span>
                  <span className="font-mono text-pink-600">{formatCurrency(ticketInfancia)}</span>
                </div>
              )}
              {ppr > 0 && (
                <div className="flex justify-between p-2 bg-pink-50 rounded">
                  <span>PPR</span>
                  <span className="font-mono text-pink-600">{formatCurrency(ppr)}</span>
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-brand-50 rounded-lg text-center">
                <p className="text-xs text-brand-600">Margem Mensal</p>
                <p className="text-2xl font-bold font-mono text-brand-700">{formatCurrency(result?.margin_amount)}</p>
                <p className="text-xs text-brand-500">{result?.margin_percentage?.toFixed(1)}%</p>
              </div>
              <div className="p-4 bg-brand-100 rounded-lg text-center">
                <p className="text-xs text-brand-600">Margem Anual ({formData.billable_months || 11}m)</p>
                <p className="text-2xl font-bold font-mono text-brand-700">
                  {formatCurrency((result?.margin_amount || 0) * (formData.billable_months || 11))}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Notas */}
        {simulation?.notes && (
          <Card className="mb-6">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <FileText className="w-4 h-4" />
                Notas
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-slate-600 whitespace-pre-wrap">{simulation.notes}</p>
            </CardContent>
          </Card>
        )}

        {/* Footer */}
        <div className="text-center text-xs text-slate-400 mt-8 print:mt-4">
          <p>Proposta gerada por Evolutt - Simulador Financeiro</p>
          <p>ID: {id}</p>
        </div>
      </main>

      {/* Print Styles */}
      <style>{`
        @media print {
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .print\\:hidden { display: none !important; }
          .print\\:py-4 { padding-top: 1rem; padding-bottom: 1rem; }
          .print\\:mb-6 { margin-bottom: 1.5rem; }
          .print\\:mt-4 { margin-top: 1rem; }
          .print\\:bg-blue-50 { background-color: rgb(239 246 255) !important; }
          .print\\:bg-slate-800 { background-color: rgb(30 41 59) !important; }
        }
      `}</style>
    </div>
  );
};

export default ProposalPage;
